import React, { useState } from "react";
import { Modal, Row, Col, Collapse, Button } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";

const { Panel } = Collapse;

type ConfirmModalProps = {
  setIsModalOpen: CallableFunction;
  isModalOpen: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  handleSubmitReferral?: CallableFunction;
  isReferralLoading: boolean;
  message: string;
  patientFormData?: any;
  flagForPatientReg: boolean;
  cardReferralError?: any;
};

const ConfirmModal: React.FC<ConfirmModalProps> = (props) => {
  const { patientFormData } = props;

  const [showAllServices, setShowAllServices] = useState(false);

  // Toggle the visibility of services
  const toggleServices = () => setShowAllServices((prev) => !prev);

  // Recursive function to render dynamic fields
  const renderDynamicData = (data: any) => {
    if (!data) return <p>N/A</p>;

    return (
      <Row gutter={[16, 16]}>
        {Object.keys(data)
          ?.filter(
            (key) =>
              key.toLowerCase() !== "document" &&
              key.toLowerCase() !== "id" &&
              key.toLowerCase() !== "schemeid"
          ) // Exclude 'document' fields
          ?.map((key) => {
            // Special fix for "services"
            if (key.toLowerCase() === "services") {
              const services = Array.isArray(data[key]) ? data[key] : [];

              return (
                <Col span={24} key={key}>
                  <p>
                    <b>SERVICES:</b>
                    <ul>
                      {services.length > 0 ? (
                        services
                          ?.slice(0, showAllServices ? services.length : 1) // Show all or only the first service
                          ?.map((service: any, index: number) => (
                            <li key={index}>
                              {Object.entries(service)
                                ?.filter(
                                  ([serviceKey]) =>
                                    serviceKey.toLowerCase() !== "id"
                                )
                                ?.map(([serviceKey, serviceValue]) => (
                                  <div key={serviceKey}>
                                    <b>
                                      {serviceKey.toLowerCase() === "name"
                                        ? "COMPONENT DETAILS"
                                        : serviceKey
                                            .replace(/([A-Z])/g, " $1")
                                            .toUpperCase()}
                                      :
                                    </b>{" "}
                                    <span>
                                      {serviceValue
                                        ? String(serviceValue)
                                        : "-"}
                                    </span>
                                  </div>
                                ))}
                            </li>
                          ))
                      ) : (
                        <li>N/A</li>
                      )}
                    </ul>
                    {services.length > 1 && (
                      <Button
                        type="link"
                        onClick={toggleServices}
                        style={{ paddingLeft: 0 }}
                      >
                        {showAllServices ? "See Less" : "See More Services"}
                      </Button>
                    )}
                  </p>
                </Col>
              );
            }

            // Special handling for ipAllowed
            if (key.toLowerCase() === "ipallowed") {
              return (
                <Col span={8} key={key}>
                  <p>
                    <b>{key.replace(/([A-Z])/g, " $1").toUpperCase()}:</b>{" "}
                    {data[key] === true ? "Yes" : "No"}
                  </p>
                </Col>
              );
            }

            // Default rendering logic for other fields
            return (
              <Col span={8} key={key}>
                <p>
                  <b>{key.replace(/([A-Z])/g, " $1").toUpperCase()}:</b>{" "}
                  {Array.isArray(data[key]) ? (
                    <ul>
                      {data?.[key]?.map((item: any, index: number) => (
                        <li key={index}>
                          {typeof item === "object"
                            ? renderDynamicData(item) // Recursively render nested objects
                            : item || "N/A"}
                        </li>
                      ))}
                    </ul>
                  ) : typeof data[key] === "object" && data[key] !== null ? (
                    renderDynamicData(data[key])
                  ) : typeof data[key] === "boolean" ? (
                    data[key] ? (
                      "Yes"
                    ) : (
                      "No"
                    ) // Boolean handling
                  ) : (
                    data[key] || "N/A"
                  )}
                </p>
              </Col>
            );
          })}
      </Row>
    );
  };

  return (
    <div className="">
      {patientFormData && (
        <Modal
          maskClosable={false}
          title={
            <>
              <ExclamationCircleFilled
                style={{ marginRight: 8, color: "#faad14" }}
              />
              CONFIRM
            </>
          }
          open={props?.isModalOpen}
          onOk={props?.handleOk}
          onCancel={props?.handleCancel}
          confirmLoading={props.isReferralLoading}
          okText="YES"
          centered
          cancelText="NO"
          width={props?.flagForPatientReg ? 800 : 500}
        >
          <p style={{ marginBottom: "0.4rem" }}>{props?.message} ?</p>
          {props?.cardReferralError ? (
            <span
              style={{
                color: "red",
                fontSize: "12px",
                fontWeight: "semi-bold",
                marginBottom: "0.6rem",
              }}
            >
              {props?.cardReferralError + ". Do You still want to proceed ?"}
            </span>
          ) : (
            <></>
          )}

          {/* Patient Details Section */}
          {props?.flagForPatientReg && (
            <Collapse defaultActiveKey={["1", "2"]}>
              <Panel header="Patient Information" key="1">
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <p>
                      <b>Name:</b>{" "}
                      {patientFormData?.patientRequest?.fullName || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>UHID:</b>{" "}
                      {patientFormData?.patientRequest?.uhidNo || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>
                        {(() => {
                          const id =
                            patientFormData?.patientRequest
                              ?.patientSchemeDetails?.schemeMetaDataId;
                          switch (id) {
                            case 1:
                            case 10:
                              return "Beneficiary ID: ";
                            case 2:
                              return "Beneficiary ID:";
                            case 3:
                              return "Medical Card ID:";
                            case 4:
                              return "Medical Card ID:";
                            default:
                              return "Identity Number:";
                          }
                        })()}
                      </b>{" "}
                      {patientFormData?.patientRequest?.identityNumber || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Age:</b>{" "}
                      {patientFormData?.patientRequest?.age || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Gender:</b>{" "}
                      {patientFormData?.patientRequest?.sex || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Email:</b>{" "}
                      {patientFormData?.patientRequest?.email || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Primary Mobile:</b>{" "}
                      {patientFormData?.patientRequest?.primaryMobile || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Secondary Mobile:</b>{" "}
                      {patientFormData?.patientRequest?.secondaryMobile ||
                        "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      <b>Scheme:</b>{" "}
                      {/* {(() => {
                        const id =
                          patientFormData?.patientRequest?.patientSchemeDetails
                            ?.schemeMetaDataId;
                        switch (id) {
                          case 1:
                            return "CGHS";
                          case 2:
                            return "ECHS";
                          case 3:
                            return "Medi Assist";
                          case 4:
                            return "STAR Health";
                          case 10:
                            return "CAPF";
                          default:
                            return "Unknown Scheme";
                        }
                      })()} */}
                      {
                        patientFormData?.patientRequest?.patientSchemeDetails
                          ?.schemeName
                      }
                    </p>
                  </Col>
                  {patientFormData?.patientRequest?.communicationConsent && (
                    <>
                      <Col span={8}>
                        <b>Communication Consent:</b>{" "}
                        {patientFormData?.patientRequest?.communicationConsent
                          ? "YES"
                          : "NO"}
                      </Col>
                      <Col span={8}>
                        <b>Primary Policy Holder:</b>{" "}
                        {patientFormData?.patientRequest?.isPrimaryPolicyHolder
                          ? "YES"
                          : "NO"}
                      </Col>
                      {patientFormData?.patientRequest?.patientSchemeDetails?.schemeName
                        ?.toLowerCase()
                        ?.includes("cghs") ||
                      patientFormData?.patientRequest?.patientSchemeDetails?.schemeName
                        ?.toLowerCase()
                        ?.includes("echs") ? (
                        <Col span={8}>
                          <b>Card Category:</b>{" "}
                          {patientFormData?.patientRequest?.cardCategory
                            ? patientFormData?.patientRequest?.cardCategory
                            : ""}
                        </Col>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </Row>
              </Panel>

              {/* Visit Details Section */}
              <Panel header="Visit Information" key="2">
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <p>
                      <b>Visit Type:</b>{" "}
                      {patientFormData?.visitInfoRequest?.visitType || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      {(patientFormData?.visitInfoRequest?.visitType === "IP" ||
                        patientFormData?.visitInfoRequest?.visitType ===
                          "EMERGENCY") && <b>Date of Admission:</b>}{" "}
                      {patientFormData?.visitInfoRequest?.visitType ===
                        "OP" && <b>Date of Visit:</b>}{" "}
                      {patientFormData?.visitInfoRequest?.doa &&
                        dayjs(patientFormData.visitInfoRequest.doa).format(
                          "DD-MM-YYYY"
                        )}
                    </p>
                  </Col>
                  {/* <Col span={8}>
                    <p>
                      <b>Date of Discharge:</b>{" "}
                      {patientFormData?.visitInfoRequest?.dod &&
                        dayjs(patientFormData.visitInfoRequest.dod).format(
                          "DD-MM-YYYY"
                        )}
                    </p>
                  </Col> */}
                  <Col span={8}>
                    <p>
                      <b>Department:</b>{" "}
                      {patientFormData?.visitInfoRequest?.department?.join(
                        ", "
                      ) || "N/A"}
                    </p>
                  </Col>
                  <Col span={8}>
                    <p>
                      {(patientFormData?.visitInfoRequest?.visitType === "IP" ||
                        patientFormData?.visitInfoRequest?.visitType?.toLowerCase() ===
                          "emergency") && <b>Ip Number:</b>}
                      {patientFormData?.visitInfoRequest?.visitType ===
                        "OP" && <b>OP Number:</b>}{" "}
                      {patientFormData?.visitInfoRequest?.opNumber || "N/A"}
                    </p>
                  </Col>
                  {patientFormData?.visitInfoRequest?.totalBillAmount && (
                    <Col span={8}>
                      <b>Expected bill Amount:</b>{" "}
                      {patientFormData?.visitInfoRequest?.totalBillAmount ||
                        "N/A"}
                    </Col>
                  )}
                  {patientFormData?.visitInfoRequest?.roomNumber && (
                    <Col span={8}>
                      <b>Room Number:</b>{" "}
                      {patientFormData?.visitInfoRequest?.roomNumber || "N/A"}
                    </Col>
                  )}
                  <Col span={8}>
                    <p>
                      <b>Doctor :</b>{" "}
                      {patientFormData?.visitInfoRequest?.doctorDetails?.split(
                        ","
                      )[1] || ""}
                    </p>
                  </Col>
                </Row>
              </Panel>

              {/* Referral Information Section */}
              {patientFormData?.patientRequest?.referralInfoRequest
                ?.echsReferralRequest && (
                <Panel header="ECHS Referral" key="3">
                  {renderDynamicData(
                    patientFormData?.patientRequest?.referralInfoRequest
                      ?.echsReferralRequest
                  )}
                </Panel>
              )}
              {patientFormData?.patientRequest?.referralInfoRequest
                ?.cghsReferralRequest &&
                patientFormData?.patientRequest?.patientSchemeDetails
                  ?.schemeMetaDataId !== 10 &&
                patientFormData?.patientRequest?.patientSchemeDetails?.schemeName?.toUpperCase() !==
                  "CAPF" && (
                  <Panel header="CGHS Referral" key="4">
                    {patientFormData?.patientRequest?.referralInfoRequest
                      ?.cghsReferralRequest ? (
                      renderDynamicData(
                        patientFormData?.patientRequest?.referralInfoRequest
                          ?.cghsReferralRequest
                      )
                    ) : (
                      <p>N/A</p>
                    )}
                  </Panel>
                )}
            </Collapse>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ConfirmModal;
